import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Page from '../../components/PageContainer/Page';
import SectionDivider from '../../components/common/SectionDivider/SectionDivider';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PhoneIcon from '@mui/icons-material/Phone';
import CircularProgress from '@mui/material/CircularProgress';
import emailjs from '@emailjs/browser';
import styles from './Contact.module.scss';
import AnimatedWrapper from '../../components/common/AnimatedWrapper/AnimatedWrapper';

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [status, setStatus] = useState({
    type: null,
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: 'loading', message: '' });

    try {
      // Sprawdź czy wszystkie wymagane pola są wypełnione
      if (!formData.name || !formData.email || !formData.subject || !formData.message) {
        throw new Error(t('contact.status.required'));
      }

      // Sprawdź czy email jest w prawidłowym formacie
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error(t('contact.status.invalid_email'));
      }

      const templateParams = {
        to_email: 'dariuszszymczak.work@gmail.com',
        from_name: formData.name,
        from_email: formData.email,
        subject: formData.subject,
        message: formData.message
      };

      const response = await emailjs.send(
        'service_2pa41w9', // Zastąp swoim Service ID
        'template_6ba6uch', // Zastąp swoim Template ID
        templateParams,
        'otrQgrZBGgw4KA3uN' // Zastąp swoim Public Key
      );

      if (response.status === 200) {
        setStatus({
          type: 'success',
          message: t('contact.status.success')
        });

        // Wyczyść formularz po udanym wysłaniu
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        throw new Error(t('contact.status.server_error'));
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || t('contact.status.error')
      });
    }
  };

  const renderStatusMessage = () => {
    if (!status.type) return null;

    return (
      <div className={`${styles.status} ${styles[`status--${status.type}`]}`}>
        {status.type === 'loading' ? (
          <CircularProgress size={24} className={styles.status__icon} />
        ) : status.type === 'success' ? (
          <CheckCircleIcon className={styles.status__icon} />
        ) : (
          <ErrorIcon className={styles.status__icon} />
        )}
        {status.type !== 'loading' && <p className={styles.status__message}>{status.message}</p>}
      </div>
    );
  };

  return (
    <Page name="contact">
      <div className={styles.contact}>
        <div className={styles.contact__header}>
          <AnimatedWrapper>
            <h2 className={styles["contact__header--title"]}>{t('contact.title')}</h2>
            <SectionDivider color="#6c757d" />
            <p className={styles["contact__header--description"]}>
              {t('contact.description')}
            </p>
          </AnimatedWrapper>
          <AnimatedWrapper delay={0.2}>
            <div className={styles.contact__phone}>
              <a href="tel:+48692683155" className={styles.contact__phoneLink}>
                <div className={styles.contact__phoneIcon}>
                  <PhoneIcon />
                </div>
                <span>+48 692 683 155</span>
              </a>
            </div>
          </AnimatedWrapper>
        </div>
        <div className={styles.contact__formWrapper}>
          <form onSubmit={handleSubmit} className={styles.contact__form}>
            <AnimatedWrapper delay={0.3}>
              <div className={styles.contact__formGroup}>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={styles.contact__input}
                  placeholder={t('contact.form.name')}
                  required
                />
              </div>
            </AnimatedWrapper>
            <AnimatedWrapper delay={0.4}>
              <div className={styles.contact__formGroup}>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={styles.contact__input}
                  placeholder={t('contact.form.email')}
                  required
                />
              </div>
            </AnimatedWrapper>
            <AnimatedWrapper delay={0.5}>
              <div className={styles.contact__formGroup}>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className={styles.contact__input}
                  placeholder={t('contact.form.subject')}
                  required
                />
              </div>
            </AnimatedWrapper>
            <AnimatedWrapper delay={0.6}>
              <div className={styles.contact__formGroup}>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className={styles.contact__textarea}
                  placeholder={t('contact.form.message')}
                  required
                />
              </div>
            </AnimatedWrapper>
            <AnimatedWrapper delay={0.7}>
              <button 
                type="submit" 
                className={styles.contact__button}
                disabled={status.type === 'loading'}
              >
                <SendIcon />
                {t('contact.form.send')}
              </button>
            </AnimatedWrapper>
          </form>
        </div>
        {renderStatusMessage()}
      </div>
    </Page>
  );
};

export default Contact; 